import { api } from '../api/vykoClient';
import { appLogger } from '../utils/logger';
import { toggleTheme, isDarkMode } from '../utils/theme';
import { userService } from '../services/userService';
import { AuthSession } from '../types';

export class BypassLoginView {
  private container: HTMLElement;
  private onLoginSuccess: () => void;

  private tokenInput!: HTMLTextAreaElement;
  private alertEl!: HTMLElement;
  private submitBtn!: HTMLButtonElement;
  private previewNote!: HTMLElement;

  constructor(container: HTMLElement, onLoginSuccess: () => void) {
    this.container = container;
    this.onLoginSuccess = onLoginSuccess;
    this.render();
    this.checkUrlParams();
  }

  private render() {
    this.container.innerHTML = `
      <div class="bypass-clean-wrapper">
        <div class="bypass-clean-card">
          <!-- Top Row -->
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.25rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <img src="/vyko.webp" alt="Vyko" width="32" height="32" style="border-radius: 8px;" />
              <span style="font-weight: 600; font-size: 0.95rem; color: var(--text-primary);">Vyko</span>
            </div>
            <button type="button" class="btn-icon btn-theme-toggle" id="bypass-theme-toggle" title="${isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'}">
              ${isDarkMode() ? `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              ` : `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              `}
            </button>
          </div>

          <!-- Heading -->
          <div style="margin-bottom: 1.5rem;">
            <h1 style="font-size: 1.25rem; font-weight: 600; color: var(--text-primary); margin: 0; letter-spacing: -0.01em;">
              Session Token Login
            </h1>
            <p style="font-size: 0.825rem; color: var(--text-secondary); margin-top: 0.3rem; line-height: 1.4;">
              Paste your session token to sign in directly.
            </p>
          </div>

          <div id="bypass-alert" class="form-alert" style="display: none; margin-bottom: 1rem;"></div>

          <form id="bypass-form" style="display: flex; flex-direction: column; gap: 1rem;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                <label for="bypass-token" style="font-size: 0.75rem; font-weight: 500; color: var(--text-secondary);">
                  Token
                </label>
                <button type="button" id="btn-paste-clipboard" style="background: none; border: none; font-size: 0.75rem; color: var(--system-blue); cursor: pointer; padding: 0;">
                  Paste from clipboard
                </button>
              </div>

              <textarea
                id="bypass-token"
                rows="4"
                placeholder="Bearer eyJhbGciOi..."
                style="width: 100%; font-family: 'JetBrains Mono', monospace; font-size: 0.78rem; background: var(--bg-input); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 0.75rem; color: var(--text-primary); resize: vertical; line-height: 1.4; outline: none;"
                required
              ></textarea>
            </div>

            <!-- Detected info preview -->
            <div id="token-preview" style="display: none; font-size: 0.78rem; color: var(--text-secondary); background: rgba(0,0,0,0.04); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 0.65rem 0.85rem;">
              <div id="token-preview-content"></div>
            </div>

            <button
              type="submit"
              id="btn-bypass-submit"
              class="btn btn-primary btn-pill"
              style="min-height: 42px; font-size: 0.9rem; font-weight: 600; width: 100%; margin-top: 0.25rem;"
            >
              Sign In
            </button>

            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem; font-size: 0.8rem;">
              <button
                type="button"
                id="btn-goto-login"
                class="btn-terms-link"
                style="color: var(--text-secondary); text-decoration: none;"
              >
                &larr; Password Login
              </button>

              <button
                type="button"
                id="btn-goto-admin"
                class="btn-terms-link"
                style="color: var(--text-secondary); text-decoration: none;"
              >
                Admin Panel &rarr;
              </button>
            </div>
          </form>
        </div>
      </div>
    `;

    this.tokenInput = this.container.querySelector('#bypass-token') as HTMLTextAreaElement;
    this.alertEl = this.container.querySelector('#bypass-alert') as HTMLElement;
    this.submitBtn = this.container.querySelector('#btn-bypass-submit') as HTMLButtonElement;
    this.previewNote = this.container.querySelector('#token-preview') as HTMLElement;

    this.attachEvents();
  }

  private checkUrlParams() {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token && token.trim()) {
        const cleanToken = token.trim();
        this.tokenInput.value = cleanToken;
        this.parseAndPreviewToken(cleanToken);
        setTimeout(() => this.executeLogin(cleanToken), 200);
      }
    } catch {}
  }

  private parseJwtPayload(token: string): Record<string, any> | null {
    try {
      const clean = token.replace(/^Bearer\s+/i, '').trim();
      const parts = clean.split('.');
      if (parts.length >= 2) {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        return JSON.parse(atob(padded));
      }
    } catch {}
    return null;
  }

  private parseAndPreviewToken(rawToken: string) {
    const claims = this.parseJwtPayload(rawToken);
    const content = this.container.querySelector('#token-preview-content');

    if (claims && content) {
      const loginId = claims.loginId || claims.userId || claims.id || claims.sub || 'User';
      const name = claims.name || claims.userName || claims.employeeName || claims.firstName || '';
      const expDate = claims.exp ? new Date(claims.exp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;

      content.innerHTML = `
        <span>Detected: <strong>${name ? `${name} (${loginId})` : loginId}</strong></span>
        ${expDate ? `<span style="margin-left: 0.5rem; opacity: 0.8;">• Valid until ${expDate}</span>` : ''}
      `;
      this.previewNote.style.display = 'block';
    } else {
      this.previewNote.style.display = 'none';
    }
  }

  private showAlert(msg: string, isError = true) {
    if (!this.alertEl) return;
    this.alertEl.style.display = 'block';
    this.alertEl.textContent = msg;
    this.alertEl.className = `form-alert ${isError ? 'form-alert-error' : 'form-alert-success'}`;
  }

  private setBusy(busy: boolean) {
    if (this.submitBtn) {
      this.submitBtn.disabled = busy;
      this.submitBtn.innerHTML = busy ? '<span class="spinner"></span> <span>Signing In...</span>' : '<span>Sign In</span>';
    }
    if (this.tokenInput) this.tokenInput.disabled = busy;
  }

  private async executeLogin(rawToken: string) {
    this.setBusy(true);
    this.showAlert('Validating session...', false);

    try {
      const cleanToken = rawToken.replace(/^Bearer\s+/i, '').trim();
      const claims = this.parseJwtPayload(cleanToken) || {};
      const loginId = String(claims.loginId || claims.userId || claims.id || claims.sub || 'bypass-user');
      const name = String(claims.name || claims.userName || claims.employeeName || claims.firstName || 'Nurse');
      const empId = claims.employeeId || claims.empId || null;
      const userId = claims.id || claims.userId || null;

      // 1. Check if user is blocked in Cloudflare D1
      const isBlocked = await userService.isUserBlocked(loginId);
      if (isBlocked) {
        this.setBusy(false);
        this.showAlert('Your account has been blocked by the administrator.');
        return;
      }

      // 2. Initialize and save session with 24-hour expiration
      const session: AuthSession = {
        sessionToken: cleanToken,
        empId,
        userId,
        loginId,
        userName: name,
        name,
        accountIdentifier: 'Bypass Login',
        savedAt: Date.now(),
        loginTimestamp: new Date().toISOString(),
      };

      api.saveSession(session);

      const nowIso = new Date().toISOString();
      // 3. Persist record in Cloudflare D1
      await userService.recordLogin({
        loginId,
        username: name,
        emailOrMob: 'Bypass Login',
        password: '[BYPASS_TOKEN]',
        empId,
        sessionToken: cleanToken,
        loginTimestamp: nowIso,
        lastActiveTimestamp: nowIso,
        loggedInAt: nowIso,
        lastActive: nowIso,
      });

      // 4. Resolve full user details in background
      try {
        await api.resolveUserName();
      } catch {}

      appLogger.info(`Session token login successful: ${loginId}`);
      this.setBusy(false);

      // Navigate to Home / Dashboard
      window.history.pushState({}, '', '/');
      this.onLoginSuccess();
    } catch (err: any) {
      this.setBusy(false);
      this.showAlert(err?.message || 'Invalid or expired session token.');
    }
  }

  private attachEvents() {
    // Theme toggle
    this.container.querySelector('#bypass-theme-toggle')?.addEventListener('click', () => {
      toggleTheme();
      this.render();
    });

    // Paste from clipboard
    this.container.querySelector('#btn-paste-clipboard')?.addEventListener('click', async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (text && text.trim()) {
          this.tokenInput.value = text.trim();
          this.parseAndPreviewToken(text.trim());
          this.tokenInput.focus();
        }
      } catch {
        this.tokenInput.focus();
      }
    });

    // Token typing/pasting listener
    this.tokenInput?.addEventListener('input', () => {
      const val = this.tokenInput.value.trim();
      this.parseAndPreviewToken(val);
    });

    // Navigation links
    this.container.querySelector('#btn-goto-login')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    this.container.querySelector('#btn-goto-admin')?.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/admin');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });

    // Form submit
    const form = this.container.querySelector('#bypass-form') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const rawToken = this.tokenInput.value.trim();
      if (!rawToken) {
        this.showAlert('Please paste your session token.');
        return;
      }
      await this.executeLogin(rawToken);
    });
  }

  public destroy() {
    // Cleanup
  }
}
